import { useMemo, useEffect, useRef } from 'react';

import debug from 'helpers/debug';
import Store from 'helpers/store';
import Cache, { cacheKey } from 'helpers/cache';

import useStore from 'hooks/useStore';
import StatsService from 'services/stats';

const log = debug('useStats');

const useStats = (statType, query, options = {}) => {
  const {
    initialStats = [],
    reset = false,
    autoFetch = false,
    refreshInterval = 0
  } = options;

  const store = useMemo(() => {
    return new Store({
      stats: initialStats,
      isLoading: autoFetch,
    }, statType.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statType]);

  const actions = useMemo(() => {
    return createStatsActions(store, statType);
  }, [store, statType]);

  const queryKeyRef = useRef(null);
  const queryKey = useMemo(() => {
    const isValid = isQueryValid(query);
    if (isValid) {
      const key = cacheKey(query);
      if (key !== queryKeyRef.current) {
        log(`Stat ${statType.name} query changed: ${queryKeyRef.current} -> ${key}`);
        queryKeyRef.current = key;
      }
    }
    return queryKeyRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (autoFetch && queryKey) {
      log(`Stat ${statType.name} is fetching: `, query);
      const t = setTimeout(() => {
        actions.getStats(query);
      });
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, autoFetch, queryKey]);

  useEffect(() => {
    if (reset) {
      store.reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, reset, queryKeyRef.current]);
  
  useEffect(() => {
    if (refreshInterval > 0) {
      return setRefreshInterval(() => {
        actions.getStats(query);
      }, refreshInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, refreshInterval, queryKey]);

  // subscribe to store
  useStore(store);

  return {
    state: store.get(),
    actions,
    store,
  };
};

const statsCache = new Cache();

const createStatsActions = (store, statType) => {
  const service = new StatsService(statType);

  return {
    async getStats(query) {
      const key = `${statType.name}:${cacheKey(query)}`;
      return Promise.resolve()
        .then(() => {
          store.set(() => ({ isLoading: true }));
          const stats = statsCache.get(key);
          if (stats) {
            store.set(() => ({ stats, isLoading: false }));
          }
          return service.loadStats(query).then(data => {
            if (!data) return;
            statsCache.set(key, data, 60000);
            store.set(() => ({ stats: data }));
            return data;
          });
        })
        .catch(err => {
          store.set(() => ({ error: err.message }));
        })
        .finally(() => {
          if (store.get().isLoading) {
            store.set(() => ({ isLoading: false }));
          }
        });
    },
  };
};

const isQueryValid = (query) => {
  return Object.keys(query).every(field => {
    return query[field] !== undefined && query[field] !== null;
  });
};

const setRefreshInterval = (cb, interval) => {
  let id = setInterval(cb, interval);
  return () => {
    clearInterval(id);
  };
};

export default useStats;
